import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listCampaigns, archiveCampaign } from '../api/campaigns';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY, EMZI_SHADOWS, getStatusPillStyle, getChipStyle, getButtonStyle } from '../styles/emziCI';

const CampaignDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('active');
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadCampaigns();
  }, [activeTab, pagination.page]);

  const loadCampaigns = async () => {
    setIsLoading(true);
    try {
      const statusMap = {
        'active': 'active',
        'upcoming': 'scheduled',
        'expired': 'expired',
        'all': null
      };

      const filters = {
        status: statusMap[activeTab],
        page: pagination.page,
        limit: pagination.limit
      };

      // Check for product_id filter from URL
      const productId = searchParams.get('product_id');
      if (productId) {
        filters.product_id = productId;
      }

      const response = await listCampaigns(filters);
      setCampaigns(response.campaigns || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        pages: response.pages || 1
      }));
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async (campaignId) => {
    if (!window.confirm('Are you sure you want to archive this campaign?')) return;

    try {
      await archiveCampaign(campaignId);
      toast.success('Campaign archived');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to archive campaign:', error);
      toast.error('Failed to archive campaign');
    }
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusDisplay = (status, endDate) => {
    const daysLeft = getDaysRemaining(endDate);
    
    let displayStatus = status;
    let displayText = status.charAt(0).toUpperCase() + status.slice(1);
    
    if (status === 'active' && daysLeft !== null) {
      displayText = `Active (${daysLeft}d left)`;
    } else if (status === 'scheduled') {
      displayStatus = 'upcoming';
      displayText = 'Upcoming';
    }
    
    return { displayStatus, displayText };
  };

  // Tab configuration
  const tabs = [
    { id: 'active', label: 'Active', testId: 'campaigns-tab-active' },
    { id: 'upcoming', label: 'Upcoming', testId: 'campaigns-tab-upcoming' },
    { id: 'expired', label: 'Expired', testId: 'campaigns-tab-expired' },
    { id: 'all', label: 'All', testId: 'campaigns-tab-all' }
  ];

  // Icons
  const icons = {
    calendar: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    people: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    region: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  };

  return (
    <div className="nexus-content" style={{ backgroundColor: EMZI_COLORS.background, minHeight: '100vh' }}>
      {/* Header Bar - Sticky - COMPACT */}
      <div 
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: EMZI_COLORS.background,
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          padding: EMZI_SPACING.compact,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ ...EMZI_TYPOGRAPHY.title, margin: 0, color: EMZI_COLORS.textPrimary, fontSize: '20px' }}>
              Campaigns
            </h1>
            <div style={{ fontSize: '9px', color: EMZI_COLORS.textMuted, marginTop: '2px' }}>
              v6.3.2-COMPACT
            </div>
          </div>
          <button
            onClick={() => navigate('/campaigns/create')}
            style={{
              ...getButtonStyle('cta'),
              minHeight: '40px',
              padding: '0 16px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
            Create
          </button>
        </div>
      </div>

      {/* Tabs - COMPACT */}
      <div 
        style={{
          borderBottom: `2px solid ${EMZI_COLORS.border}`,
          padding: `0 ${EMZI_SPACING.compact}`,
          backgroundColor: EMZI_COLORS.background,
          overflowX: 'auto',
          display: 'flex',
          gap: EMZI_SPACING.md
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={tab.testId}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: `${EMZI_SPACING.sm} 0`,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${EMZI_COLORS.primary}` : '3px solid transparent',
              color: activeTab === tab.id ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
              fontSize: '13px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: '-2px',
              transition: 'all 0.2s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaign List - COMPACT */}
      <div style={{ padding: EMZI_SPACING.compact }}>
        {isLoading ? (
          // Skeleton Loading - COMPACT
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.sm }}>
            {[1, 2, 3].map((i) => (
              <div
                key={`item-${i}`}
                style={{
                  backgroundColor: EMZI_COLORS.surface,
                  borderRadius: EMZI_RADIUS.md,
                  padding: EMZI_SPACING.compact,
                  height: '100px',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}
              />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          // Empty State - COMPACT
          <div 
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: EMZI_COLORS.textSecondary
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: EMZI_SPACING.sm }}>📢</div>
            <p style={{ ...EMZI_TYPOGRAPHY.h2, marginBottom: EMZI_SPACING.xs, fontSize: '16px' }}>
              No campaigns here yet
            </p>
            <p style={{ ...EMZI_TYPOGRAPHY.body, marginBottom: EMZI_SPACING.md, fontSize: '13px' }}>
              Create your first campaign to get started
            </p>
            <button
              onClick={() => navigate('/campaigns/create')}
              style={{
                ...getButtonStyle('primary'),
                fontSize: '13px'
              }}
            >
              + Create Campaign
            </button>
          </div>
        ) : (
          // Campaign Cards - COMPACT
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.sm }}>
            {campaigns.map((campaign) => {
              const { displayStatus, displayText } = getStatusDisplay(
                campaign.status,
                campaign.timeline?.end_date
              );

              return (
                <div
                  key={campaign.campaign_id}
                  data-testid="campaign-card"
                  style={{
                    backgroundColor: EMZI_COLORS.background,
                    border: `1px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    padding: EMZI_SPACING.compact,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    maxHeight: '110px',
                    transition: 'box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                >
                  {/* Row 1: Name + Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2 style={{ 
                        ...EMZI_TYPOGRAPHY.h2, 
                        margin: 0, 
                        color: EMZI_COLORS.textPrimary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '16px'
                      }}>
                        {campaign.campaign_name}
                      </h2>
                      <p style={{ ...EMZI_TYPOGRAPHY.caption, margin: '2px 0 0 0', color: EMZI_COLORS.textMuted, fontSize: '11px' }}>
                        {campaign.base_product_name || 'Product'}
                      </p>
                    </div>
                    <div style={{ ...getStatusPillStyle(displayStatus), fontSize: '11px', padding: '2px 8px', flexShrink: 0 }}>
                      {displayText}
                    </div>
                  </div>

                  {/* Row 2: Meta Chips - Inline */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '11px' }}>
                    {campaign.timeline && (
                      <div style={{ ...getChipStyle(), fontSize: '11px', padding: '2px 6px' }}>
                        {icons.calendar}
                        <span>
                          {new Date(campaign.timeline.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(campaign.timeline.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                    {campaign.assigned_teams && campaign.assigned_teams.length > 0 && (
                      <div style={{ ...getChipStyle(), fontSize: '11px', padding: '2px 6px' }}>
                        {icons.people}
                        <span>{campaign.assigned_teams.length} team{campaign.assigned_teams.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {campaign.region && (
                      <div style={{ ...getChipStyle(), fontSize: '11px', padding: '2px 6px' }}>
                        {icons.region}
                        <span>{campaign.region}</span>
                      </div>
                    )}
                  </div>

                  {/* Row 3: Icon Action Buttons */}
                  <div 
                    style={{ 
                      display: 'flex', 
                      gap: '6px', 
                      marginTop: '2px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/campaigns/${campaign.campaign_id}`);
                      }}
                      title="Edit"
                      style={{
                        ...EMZI_BUTTON_STYLES.icon,
                        backgroundColor: EMZI_COLORS.primary,
                        color: EMZI_COLORS.textWhite,
                        border: 'none',
                        flex: '1'
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/spider-web?campaign_id=${campaign.campaign_id}`);
                      }}
                      title="View Graph"
                      style={{
                        ...EMZI_BUTTON_STYLES.icon,
                        flex: '1'
                      }}
                    >
                      🕸️
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.success('Duplicate feature coming soon');
                      }}
                      title="Duplicate"
                      style={{
                        ...EMZI_BUTTON_STYLES.icon,
                        flex: '1'
                      }}
                    >
                      📋
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(campaign.campaign_id);
                      }}
                      title="Archive"
                      style={{
                        ...EMZI_BUTTON_STYLES.icon,
                        color: EMZI_COLORS.error,
                        borderColor: EMZI_COLORS.error,
                        flex: '1'
                      }}
                    >
                      🗄️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination - COMPACT */}
        {!isLoading && pagination.pages > 1 && (
          <div 
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              marginTop: EMZI_SPACING.md,
              padding: EMZI_SPACING.sm
            }}
          >
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              style={{
                ...getButtonStyle('small'),
                opacity: pagination.page === 1 ? 0.5 : 1,
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              ← Prev
            </button>
            <span style={{ ...EMZI_TYPOGRAPHY.body, alignSelf: 'center', padding: '0 8px', fontSize: '12px' }}>
              {pagination.page}/{pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
              disabled={pagination.page >= pagination.pages}
              style={{
                ...getButtonStyle('small'),
                opacity: pagination.page >= pagination.pages ? 0.5 : 1,
                cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDashboard;
